import { useMemo } from "react";
import { Link } from "react-router-dom";
import type { Site } from "../lib/types";

/**
 * Schematische EU-Karte mit Country-Boxes (kein echtes Geo-Backend).
 * Pins werden in den jeweiligen Country-Boxen verteilt — visuell ausreichend
 * für den "wir kennen euch alle"-Effekt, ohne Tile-Server-Dependency.
 *
 * Phase 2: Echte Leaflet/Mapbox-Map mit OSM-Tiles.
 */

type CountryBox = {
  code: string;
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

const COUNTRY_BOXES: CountryBox[] = [
  { code: "ES", name: "Spain", x: 60, y: 380, w: 180, h: 130 },
  { code: "FR", name: "France", x: 230, y: 280, w: 170, h: 150 },
  { code: "DE", name: "Germany", x: 380, y: 200, w: 140, h: 140 },
  { code: "IT", name: "Italy", x: 380, y: 350, w: 100, h: 180 },
  { code: "AT", name: "Austria", x: 480, y: 280, w: 90, h: 60 },
  { code: "CZ", name: "Czechia", x: 510, y: 210, w: 80, h: 60 },
  { code: "SK", name: "Slovakia", x: 580, y: 240, w: 70, h: 50 },
  { code: "PL", name: "Poland", x: 530, y: 130, w: 130, h: 80 },
];

const STATUS_COLOR: Record<string, string> = {
  active: "fill-emerald-400",
  planned: "fill-sky-400",
  provisioning: "fill-amber-400",
  maintenance: "fill-amber-400",
  decommissioned: "fill-zinc-600",
};

export function EuMap({ sites }: { sites: Site[] }) {
  const sitesByCountry = useMemo(() => {
    const out: Record<string, Site[]> = {};
    for (const s of sites) {
      (out[s.country] ??= []).push(s);
    }
    return out;
  }, [sites]);

  return (
    <div className="relative w-full">
      <svg viewBox="0 0 720 560" className="w-full h-auto bg-grid rounded-lg border border-zinc-800">
        {/* Sea background */}
        <rect x="0" y="0" width="720" height="560" fill="oklch(0.16 0.02 240)" />

        {/* Country shapes */}
        {COUNTRY_BOXES.map((cb) => (
          <g key={cb.code}>
            <rect
              x={cb.x}
              y={cb.y}
              width={cb.w}
              height={cb.h}
              rx="6"
              className="fill-zinc-900 stroke-zinc-700"
              strokeWidth="1.5"
            />
            <text
              x={cb.x + 6}
              y={cb.y + 14}
              className="fill-zinc-500 font-mono"
              fontSize="10"
            >
              {cb.code}
            </text>
            <text
              x={cb.x + cb.w - 6}
              y={cb.y + 14}
              textAnchor="end"
              className="fill-zinc-400 font-mono"
              fontSize="11"
              fontWeight="600"
            >
              {sitesByCountry[cb.code]?.length ?? 0}
            </text>
          </g>
        ))}

        {/* Pins */}
        {COUNTRY_BOXES.flatMap((cb) => {
          const csites = sitesByCountry[cb.code] ?? [];
          return csites.map((s, i) => {
            const cols = Math.ceil(Math.sqrt(csites.length));
            const rows = Math.ceil(csites.length / cols);
            const col = i % cols;
            const row = Math.floor(i / cols);
            const px = cb.x + 14 + col * ((cb.w - 24) / Math.max(1, cols - 1 || 1));
            const py = cb.y + 28 + row * ((cb.h - 36) / Math.max(1, rows - 1 || 1));
            const colorCls = STATUS_COLOR[s.status] ?? "fill-zinc-400";
            const r = s.repeaters > 0 ? 3 : 2;
            return (
              <Link key={s.code} to={`/sites/${s.code}`}>
                <circle
                  cx={px}
                  cy={py}
                  r={r}
                  className={`${colorCls} cursor-pointer hover:opacity-100`}
                  opacity="0.85"
                >
                  <title>
                    {s.code} · {s.bu} · {s.site_type}
                    {s.repeaters ? ` · ${s.repeaters}x repeater` : ""}
                  </title>
                </circle>
              </Link>
            );
          });
        })}
      </svg>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] font-mono text-zinc-500">
        <Legend dot="bg-emerald-400" label="active" />
        <Legend dot="bg-sky-400" label="planned" />
        <Legend dot="bg-amber-400" label="provisioning / maintenance" />
        <Legend dot="bg-zinc-500" label="decommissioned" />
        <span className="ml-auto">schematic view · phase 2: real geo-map</span>
      </div>
    </div>
  );
}

function Legend({ dot, label }: { dot: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}
